function randomIndex (length, random) {
  const value = Number(random())
  const normalized = Number.isFinite(value) ? Math.min(Math.max(value, 0), 0.9999999999999999) : 0
  return Math.floor(normalized * length)
}

export const DEPOSIT_CHANNEL_LOCK_TTL_MS = 30 * 60 * 1000

const DEPOSIT_CHANNEL_LOCK_PREFIX = 'deposit-channel-lock:v1:'

function normalizeKeyPart (value) {
  return String(value === null || value === undefined ? '' : value).trim().toLowerCase()
}

function depositChannelLockStorageKey (userKey) {
  const normalizedUserKey = String(userKey || '').trim()
  return normalizedUserKey ? `${DEPOSIT_CHANNEL_LOCK_PREFIX}${normalizedUserKey}` : ''
}

export function getDepositCandidateKey (candidate) {
  if (!candidate || typeof candidate !== 'object') return ''
  if (candidate.type === 'payment') {
    return `payment:${normalizeKeyPart(candidate.channel && candidate.channel.type)}`
  }
  if (candidate.type !== 'bank') return ''

  const bank = candidate.bank || {}
  const bankId = bank.id || bank.key
  if (bankId) return `bank:${normalizeKeyPart(bankId)}`

  const provider = bank.payment_provider || bank.deposit_source || bank.type_deposit
  if (provider) return `bank-provider:${normalizeKeyPart(provider)}`

  const bankName = normalizeKeyPart(bank.bank_name || bank.bank)
  const accountNumber = normalizeKeyPart(bank.account_no || bank.account)
  return bankName && accountNumber ? `bank-account:${bankName}:${accountNumber}` : ''
}

export function readDepositChannelLock (storage, userKey, now = Date.now()) {
  const storageKey = depositChannelLockStorageKey(userKey)
  if (!storage || !storageKey) return null

  try {
    const lock = JSON.parse(storage.getItem(storageKey) || 'null')
    const lastCreatedAt = Number(lock && lock.lastCreatedAt)
    const candidateKey = normalizeKeyPart(lock && lock.candidateKey)
    const expired = !Number.isFinite(lastCreatedAt) || now - lastCreatedAt >= DEPOSIT_CHANNEL_LOCK_TTL_MS
    if (!candidateKey || expired) {
      storage.removeItem(storageKey)
      return null
    }
    return { candidateKey, lastCreatedAt }
  } catch (error) {
    storage.removeItem(storageKey)
    return null
  }
}

export function saveDepositChannelLock (storage, userKey, candidate, now = Date.now()) {
  const storageKey = depositChannelLockStorageKey(userKey)
  const candidateKey = getDepositCandidateKey(candidate)
  if (!storage || !storageKey || !candidateKey) return false

  storage.setItem(storageKey, JSON.stringify({ candidateKey, lastCreatedAt: now }))
  return true
}

export function clearDepositChannelLock (storage, userKey) {
  const storageKey = depositChannelLockStorageKey(userKey)
  if (!storage || !storageKey) return
  storage.removeItem(storageKey)
}

export function buildBalancedDepositCandidates (paymentChannels = [], receiverBanks = [], random = Math.random) {
  const candidates = [
    ...paymentChannels.map(channel => ({ type: 'payment', channel })),
    ...receiverBanks.map(bank => ({ type: 'bank', bank }))
  ]

  for (let index = candidates.length - 1; index > 0; index -= 1) {
    const swapIndex = randomIndex(index + 1, random)
    const current = candidates[index]
    candidates[index] = candidates[swapIndex]
    candidates[swapIndex] = current
  }

  return candidates
}

export function buildLockedDepositCandidates (paymentChannels = [], receiverBanks = [], options = {}) {
  const candidates = buildBalancedDepositCandidates(paymentChannels, receiverBanks, options.random || Math.random)
  const lock = readDepositChannelLock(options.storage, options.userKey, options.now === undefined ? Date.now() : options.now)
  if (!lock) return candidates

  const lockedIndex = candidates.findIndex(candidate => getDepositCandidateKey(candidate) === lock.candidateKey)
  if (lockedIndex < 0) {
    clearDepositChannelLock(options.storage, options.userKey)
    return candidates
  }
  if (lockedIndex === 0) return candidates

  const [lockedCandidate] = candidates.splice(lockedIndex, 1)
  candidates.unshift(lockedCandidate)
  return candidates
}
